import { Entity } from '@api/api/entity-schema';
import { PriceHistory } from '@api/api/price-schema';
import { mapEntity } from '@api/mappers/mapEntity';
import { globalPriceFormatter } from '@terminal/utils/asset-price/assetPrice';
import clsx from 'clsx';
import { formatInTimeZone } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';

import { PriceSummaryDisplay } from '../components/PriceSummaryDisplay';
import styles from './EarningsMarketReactionPrice.module.scss';

const EASTERN_TIMEZONE = 'America/New_York';

export interface EarningsMarketReactionPriceProps {
  entity: Entity;
  eventProportionChange?: number;
  prevCloseData?: PriceHistory[number];
  lastKnownPrice?: number;
  lastKnownDate?: string;
  isDailyFallback?: boolean;
}

export const EarningsMarketReactionPrice = ({
  entity,
  eventProportionChange,
  prevCloseData,
  lastKnownPrice,
  lastKnownDate,
  isDailyFallback = false,
}: EarningsMarketReactionPriceProps) => {
  const { t } = useTranslation(['analyze', 'insights']);

  if (eventProportionChange === undefined) {
    return null;
  }

  const mappedEntity = mapEntity(entity);

  const formatPrice = (price: number) =>
    globalPriceFormatter({
      entity: mappedEntity,
      price,
      priceSuffix: '',
      shouldShowCurrency: false,
    });

  const priceColumn = {
    price: lastKnownPrice ? formatPrice(lastKnownPrice) : undefined,
    currency: mappedEntity.currency,
    label: lastKnownDate && (
      <span className={clsx(styles.dateSpan)}>
        {isDailyFallback
          ? t('insights:catalyst.drawer.asOfDateClose', {
              date: formatInTimeZone(new Date(lastKnownDate), 'UTC', 'MMM d'),
            })
          : t('insights:catalyst.drawer.asOfDate', {
              date: formatInTimeZone(
                new Date(lastKnownDate),
                EASTERN_TIMEZONE,
                "MMM d, h a 'ET'"
              ),
            })}
      </span>
    ),
  };

  return (
    <div data-testid="market-reaction-price">
      <div className={clsx(styles.proportionChangeContainer)}>
        <PriceSummaryDisplay
          priceColumn={priceColumn}
          percentageChange={eventProportionChange}
          prevCloseTime={prevCloseData?.time}
        />
      </div>
    </div>
  );
};
